import {
    format, isSameDay, isWithinInterval, isBefore, isEqual, differenceInSeconds,
    intervalToDuration,
} from 'date-fns';

/**
 * @param {Date} date
 * @param {number} roundedMinutes
 * @returns {Date} date Rounded up by roundedMinutes
 */
export const roundUpDateTimeByXMinutes = (date, roundedMinutes) =>
    new Date(Math.ceil(date.getTime() / (1000 * 60 * roundedMinutes)) * 1000 * 60 * roundedMinutes);

/**
 * @param {number} roundedMinutes
 * @param {number} addedMinutes
 * @returns {Date} Date Rounded up by roundedMinutes plus addedMinutes
 */
export const getCurrentTimeRoundedUpByXAndPlusYMinutes = (roundedMinutes, addedMinutes) =>
    new Date(roundUpDateTimeByXMinutes(new Date(), roundedMinutes).getTime() + 1000 * 60 * addedMinutes);

/**
 * Wrapper to Fns `isWithinInterval` considering all dates with time zero.
 * @typedef {{ start: Date; end: Date; }} RangeDateFns
 * @param {string|Date} comparingDate
 * @param {RangeDateFns} range
 * @returns {boolean} `true` if the given date is within the period.
 */
export const isWithinDates = (comparingDate, range) =>
    isWithinInterval(
        new Date(comparingDate).setHours(0, 0, 0, 0),
        {
            start: new Date(range.start).setHours(0, 0, 0, 0),
            end: new Date(range.end).setHours(0, 0, 0, 0),
        },
    );

/**
 * @param {Date|string|number} start
 * @param {Date|string|number} end
 */
export const rangeToIndividualDatesArray = (start, end) => {
    const rangeDates = [];
    const endDate = new Date(end);

    for (const date = new Date(start); isBefore(date, endDate) || isEqual(date, endDate); date.setDate(date.getDate() + 1)) {
        rangeDates.push(new Date(date));
    }
    return rangeDates;
};

/**
 * Recursive function to make adjacent ranges from dates
 * @param {(Date|string|number)[]} dates
 * @param {(Date|string|number)=} startDate
 * @param {{ startDate: Date|string|number; endDate: Date|string|number; }[]=} ranges
 */
export const adjacentRangesFromDates = (dates, ranges = [], startDate) => {
    if (!dates.length) {
        return ranges;
    }

    const [currentDate, nextDate] = dates;
    startDate = startDate || currentDate;
    const completedRange = [...ranges, { startDate, endDate: currentDate }];

    if (!nextDate) { // recursive ending condition
        return completedRange;
    }

    const currentAsDate = new Date(currentDate);
    currentAsDate.setDate(currentAsDate.getDate() + 1);
    const isNextDateSameAsNextDay = isSameDay(new Date(nextDate), currentAsDate);
    const remainingDates = dates.slice(1);
    return isNextDateSameAsNextDay
        ? adjacentRangesFromDates(remainingDates, ranges, startDate)
        : adjacentRangesFromDates(remainingDates, completedRange);
};

/**
 *
 * @param {Date|string|number} date1
 * @param {Date|string|number} date2
 * @returns
 */
export const isBeforeOrEqual = (date1, date2) =>
    differenceInSeconds(new Date(date1), new Date(date2)) <= 0;

/**
 *
 * @param {Date|string|number} date1
 * @param {Date|string|number} date2
 * @returns boolean: Date1 >= Date2
 */
export const isAfterOrEqual = (date1, date2) =>
    differenceInSeconds(new Date(date1), new Date(date2)) >= 0;

/**
 *
 * @param {Date|string|number} date
 * @param {Date|string|number} start
 * @param {Date|string|number} end
 * @returns
 */
export const isBetweenOrEqual = (date, start, end) =>
    isAfterOrEqual(date, start) && isBeforeOrEqual(date, end);

/**
 * @param {Date|number} start1
 * @param {Date|number} end1
 * @param {Date|number} start2
 * @param {Date|number} end2
 */
export const isRangeOverlapping = (start1, end1, start2, end2) =>
    isAfterOrEqual(end1, start2) && isAfterOrEqual(end2, start1);

/**
 * @param {Date|string|number} date
 * @param {number} days
 */
export const addDays = (date, days) => {
    const dateTime = new Date(date);
    return new Date(dateTime.setDate(dateTime.getDate() + days));
};

/** @param {Date|string|number|undefined} date */
export const getHourAndMinuteOnly = (date) => (date ? format(new Date(date), 'HH:mm') : '');

/**
 * Get the difference even when `dateTime2` is lesser than `dateTime1` (am period)
 * @param {Date} dateTime1
 * @param {Date} dateTime2
 */
export const getCompleteTimeDifference = (dateTime1, dateTime2) => {
    if (!dateTime1 || !dateTime2) {
        return {};
    }

    const today = new Date();
    const time1WithCurrentDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        new Date(new Date(dateTime1)).getHours(),
        new Date(new Date(dateTime1)).getMinutes(),
    );

    const time2WithCurrentDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        new Date(new Date(dateTime2)).getHours(),
        new Date(new Date(dateTime2)).getMinutes(),
    );

    let lesserDate = time1WithCurrentDate;
    let greaterDate = time2WithCurrentDate;

    if (time1WithCurrentDate > time2WithCurrentDate) {
        greaterDate = time1WithCurrentDate;
        lesserDate = addDays(time2WithCurrentDate, 1);
    }

    return intervalToDuration({ start: greaterDate, end: lesserDate });
};
